import React from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  alpha,
  Avatar,
  Chip,
  Tooltip,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import SecurityIcon from "@mui/icons-material/Security";
import {
  capitalizeFirstLetter,
  formatDateWithYear,
} from "../../../utils/hooks/dataHandler";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import { formatUserType } from "../../../admin/utils";
import PaymentIcon from "@mui/icons-material/Payment";

const ProfileStats = ({ user }) => {
  const { user_type, user_role, subscription } = user;

  const stats = [
    {
      label: "Account Status",
      value: capitalizeFirstLetter(subscription?.status),
      icon: <SecurityIcon />,
      color:
        subscription && subscription.status === "active"
          ? "success"
          : "warning",
    },
    {
      label: "User Type",
      value: capitalizeFirstLetter(formatUserType(user_type)),
      icon: <AccessibilityIcon />,
      color: "primary",
    },
    {
      label: "Role",
      value: capitalizeFirstLetter(user_role),
      icon: <GroupIcon />,
      color: "secondary",
    },
  ];

  const StatCard = ({ stat }) => (
    <Paper
      elevation={0}
      role='status'
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        gap: 2,
        transition: "all 0.25s ease-in-out",
        cursor: "default",
        "&:hover": {
          borderColor: `${stat.color}.main`,
          boxShadow: 3,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Avatar
        variant='rounded'
        sx={{
          bgcolor: (theme) => alpha(theme.palette[stat.color].main, 0.15),
          color: `${stat.color}.main`,
          width: 44,
          height: 44,
          fontSize: 22,
        }}
      >
        {stat.icon}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ fontWeight: 500, letterSpacing: 0.3 }}
          noWrap
        >
          {stat.label}
        </Typography>
        <Typography
          variant='h6'
          sx={{ fontWeight: 600, color: "text.primary" }}
          noWrap
        >
          {stat.value}
        </Typography>
      </Box>
    </Paper>
  );
  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <PaymentIcon color='primary' />
          Subscription Package
        </Typography>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 0.5 }}>
          <Typography fontWeight={700}>
            {user?.subscription?.plan_name || "N/A"}
          </Typography>
          <Tooltip
            title={
              user?.subscription?.verified
                ? "Your payment method has been successfully verified."
                : "Your payment method is not verified yet. Please complete verification."
            }
          >
            <Chip
              size='small'
              label={user?.subscription?.verified ? "Verified" : "Unverified"}
              color={user?.subscription?.verified ? "success" : "error"}
            />
          </Tooltip>
        </Stack>
        {user?.subscription?.status === "trialing" && (
          <Typography variant='body2' color='text.secondary'>
            Trial end: {formatDateWithYear(user?.subscription?.trial_end)}
          </Typography>
        )}
      </Paper>

      {/* Account Statistics */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TrendingUpIcon color='primary' />
          Account Overview
        </Typography>

        <Stack spacing={2}>
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} />
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ProfileStats;
